import localForage from 'localforage';
import { persistReducer } from 'redux-persist';
import { createSelector } from '@reduxjs/toolkit';
import { type RootState } from 'app/store';
import { type WorkflowGroup, type WorkflowGroupsState, type WorkflowGroupsActionTypes } from 'app/store/types';

const initialState: WorkflowGroupsState = {};

const workflowGroupsReducer = (state = initialState, action: WorkflowGroupsActionTypes) => {
	switch (action.type) {
		case 'GET_LICENSE_GROUP_DATA_SUCCESS': {
			const {
				licenseGroupId,
				data: { workflowGroups },
			} = action.payload;

			let updatedGroups;
			if (workflowGroups) {
				// maintain order since order is not returned in that api call
				const groups = { ...state[licenseGroupId]?.byId };
				updatedGroups = Object.keys(workflowGroups).reduce((result: { [key: string]: WorkflowGroup }, id) => {
					const existingGroup = groups[id];
					result[id] = existingGroup?.groupOrder
						? {
								...workflowGroups[id],
								groupOrder: existingGroup.groupOrder,
							}
						: workflowGroups[id];
					return result;
				}, {});
			}

			return {
				...state,
				[licenseGroupId]: {
					...state[licenseGroupId],
					...(updatedGroups && { byId: updatedGroups }),
				},
			};
		}
		case 'GET_WORKFLOW_GROUP_ORDER_SUCCESS': {
			const { order, licenseGroupId } = action.payload;
			const groups = { ...state[licenseGroupId].byId };

			// order list may not contain all groups
			const existingGroupIds = order.filter((groupId: string) => groups[groupId]);
			const updatedGroups: { [key: string]: WorkflowGroup } = {};

			existingGroupIds.forEach((groupId: string, idx: number) => {
				const group = { ...groups[groupId] };
				group.groupOrder = idx + 1;
				updatedGroups[groupId] = group;
			});

			// assign other group order for non existing groups in order array
			const nonOrderedGroups = Object.keys(groups).filter((groupId) => !existingGroupIds.includes(groupId));

			nonOrderedGroups.forEach((groupId, idx) => {
				const group = { ...groups[groupId] };
				group.groupOrder = existingGroupIds.length + idx + 1;
				updatedGroups[groupId] = group;
			});

			return {
				...state,
				[licenseGroupId]: {
					byId: updatedGroups,
				},
			};
		}
		case 'LOGGED_OUT_USER':
		case 'PURGE_STATE':
			return initialState;
		default: {
			return state;
		}
	}
};

export default persistReducer(
	{
		key: 'workflowGroups',
		storage: localForage,
		whitelist: localStorage.getItem('persist') ? undefined : [],
	},
	workflowGroupsReducer,
);

// Selectors

export const getWorkflowGroupsById = ({ app: { selectedLicenseGroupId }, workflowGroups }: RootState) =>
	workflowGroups[selectedLicenseGroupId].byId;

export const getWorkflowGroups = createSelector([getWorkflowGroupsById], (workflowGroupsById) =>
	Object.values(workflowGroupsById),
);

export const getWorkflowGroupById = (workflowGroupId: WorkflowGroup['id']) => (state: RootState) =>
	getWorkflowGroupsById(state)[workflowGroupId];
